import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useGetTenant } from '@/api/trafikportalen';
import Button from '@/components/core/Button';
import { DocumentHead } from '@/components/core/DocumentHead';
import Header from '@/components/Header/Header';
import { Loading } from '@/components/Loading.tsx';
import MarkdownContent from '@/MarkdownContent.tsx';
import { soleLink, type SoleLink } from '@/helpers/soleLink';
import type { FaqAudience } from '@/helpers/visibleFaqs';
import { useVisibleFaqAudiences } from '@/hooks/useVisibleFaqAudiences';
import { IconExternalLink } from '@/icons';

export const Route = createFileRoute('/$tenantId/_layout/faq/')({
  component: Faq,
});

const FaqLinkButton = ({ href, label }: SoleLink & { label: string }) => {
  const { formatMessage } = useIntl();

  return (
    <Button
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="filled"
      endIcon={<IconExternalLink aria-hidden="true" />}
      aria-label={formatMessage(
        { defaultMessage: '{label} (öppnas i ny flik)' },
        { label },
      )}
    >
      {label}
    </Button>
  );
};

const nextTabIndex = (current: number, length: number, key: string) => {
  if (key === 'ArrowRight') return (current + 1) % length;
  if (key === 'ArrowLeft') return (current - 1 + length) % length;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return null;
};

function Faq() {
  const { data: tenant, isLoading } = useGetTenant();
  const audiences = useVisibleFaqAudiences();
  const { formatMessage } = useIntl();
  const [selectedAudience, setSelectedAudience] =
    useState<FaqAudience>('company');
  const tabRefs = useRef<
    Partial<Record<FaqAudience, HTMLButtonElement | null>>
  >({});

  const activeAudience =
    audiences?.find((audience) => audience === selectedAudience) ??
    audiences?.[0];
  const showTabs = (audiences?.length ?? 0) > 1;

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!audiences || !activeAudience || audiences.length < 2) return;

      const next = nextTabIndex(
        audiences.indexOf(activeAudience),
        audiences.length,
        event.key,
      );
      if (next === null) return;

      const audience = audiences[next];
      if (!audience) return;

      event.preventDefault();
      setSelectedAudience(audience);
      tabRefs.current[audience]?.focus();
    },
    [activeAudience, audiences],
  );

  if (isLoading || !tenant || audiences === undefined) {
    return <Loading />;
  }

  const emptyMessage = formatMessage({
    defaultMessage: 'Det finns ingen FAQ ännu.',
  });

  const content: Record<
    FaqAudience,
    { title: string; markdown: string; link: SoleLink | null }
  > = {
    company: {
      title: formatMessage({ defaultMessage: 'Företag' }),
      markdown: tenant.faq_company,
      link: soleLink(tenant.faq_company),
    },
    school: {
      title: formatMessage({ defaultMessage: 'Skola' }),
      markdown: tenant.faq_school,
      link: soleLink(tenant.faq_school),
    },
  };

  const visible = audiences.map((audience) => content[audience]);
  const onlyLinks =
    visible.length > 0 && visible.every((section) => section.link);
  const active = activeAudience ? content[activeAudience] : undefined;
  const markdown = active?.markdown.trim() || emptyMessage;

  return (
    <div className="w-full min-w-0 overflow-y-auto">
      <DocumentHead
        titleKey="page.faq.title"
        descriptionKey="page.faq.description"
      />
      <Header title={<FormattedMessage defaultMessage="FAQ" />} />
      <div className="mb-12 flex w-full min-w-0 flex-col items-center gap-4 p-4 md:p-8 lg:mb-0">
        {onlyLinks ? (
          <div className="flex w-full min-w-0 max-w-panel-full flex-wrap gap-3">
            {audiences.map((audience) => {
              const section = content[audience];
              if (!section.link) return null;
              return (
                <FaqLinkButton
                  key={audience}
                  href={section.link.href}
                  label={section.link.label ?? section.title}
                />
              );
            })}
          </div>
        ) : (
          <>
            {showTabs && (
              <div
                role="tablist"
                aria-label={formatMessage({ defaultMessage: 'FAQ' })}
                className="flex w-full min-w-0 max-w-panel-full flex-wrap gap-1"
                onKeyDown={handleTabKeyDown}
              >
                {audiences.map((audience) => {
                  const selected = audience === activeAudience;
                  return (
                    <Button
                      key={audience}
                      id={`faq-tab-${audience}`}
                      ref={(node) => {
                        tabRefs.current[audience] =
                          node instanceof HTMLButtonElement ? node : null;
                      }}
                      role="tab"
                      variant={selected ? 'filled' : 'ghost'}
                      aria-selected={selected}
                      aria-controls={`faq-panel-${audience}`}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setSelectedAudience(audience)}
                    >
                      {content[audience].title}
                    </Button>
                  );
                })}
              </div>
            )}
            <div
              id={activeAudience ? `faq-panel-${activeAudience}` : undefined}
              role={showTabs ? 'tabpanel' : undefined}
              aria-labelledby={
                showTabs && activeAudience
                  ? `faq-tab-${activeAudience}`
                  : undefined
              }
              className="flex w-full min-w-0 max-w-panel-full flex-col"
            >
              {active?.link ? (
                <FaqLinkButton
                  href={active.link.href}
                  label={active.link.label ?? active.title}
                />
              ) : (
                <MarkdownContent markdownText={markdown} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
